/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import TypesenseInstantSearchAdapter, {
  TypesenseInstantsearchAdapterOptions,
} from "typesense-instantsearch-adapter"
import { useMutation } from "urql"
import { SearchFieldId } from "./Constants"
import { TypesenseContext } from "./TypesenseProvider"
import { getTypesenseKey, UserContext } from "./getTypesenseKey"

export interface useUiTypesenseClientProps {
  context?: UserContext
  collectionId?: SearchFieldId
  collectionName?: string
  usePermission?: boolean
  searchParams?: object
}

interface TypesenseHookReturn {
  adapter?: TypesenseInstantSearchAdapter
  adapterProps?: TypesenseInstantsearchAdapterOptions
  apiKey: string
  exportCertificatesKey?: string
}

const EightHours = 28800000 // 8 hours in milliseconds
const FiveMinutes = 5 * 60 * 1000 // 5 minutes in milliseconds

export const TYPESENSE_APIKEY_IN_STORAGE = "typesense-apiKey"
export const TYPESENSE_PARTY_APIKEY_IN_STORAGE = "typesense-party-apiKey"

export const clearApiKey = () => {
  localStorage.removeItem(TYPESENSE_APIKEY_IN_STORAGE)
  localStorage.removeItem(TYPESENSE_PARTY_APIKEY_IN_STORAGE)
}

export function useTypesenseApiKey({
  mutation,
  localStorageKey,
}: {
  mutation: string
  localStorageKey: string
}) {
  const [apiKey, setApiKey] = useState("")
  const [isRefreshing, setIsRefreshing] = useState(false)
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null)
  const isRefreshingRef = useRef(false)
  const [newSearchApiKeyResult, updateNewSearchApiKeyResult] =
    useMutation(mutation)

  useEffect(() => {
    isRefreshingRef.current = isRefreshing
  }, [isRefreshing])

  const refreshKeys = useCallback(() => {
    if (isRefreshingRef.current) return
    setIsRefreshing(true)
    localStorage.removeItem(localStorageKey)
    updateNewSearchApiKeyResult()
  }, [localStorageKey])

  const scheduleRefresh = useCallback(
    (expirationTime: string) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }

      const expireTime = new Date(expirationTime).getTime()
      const now = new Date().getTime()
      const refreshTime = expireTime - now - FiveMinutes

      if (refreshTime > 0) {
        refreshTimerRef.current = setTimeout(() => {
          if (isRefreshingRef.current) return
          setIsRefreshing(true)
          localStorage.removeItem(localStorageKey)
          updateNewSearchApiKeyResult()
        }, refreshTime)
      }
    },
    [localStorageKey],
  )

  useEffect(() => {
    // Skip if no mutation provided
    if (!mutation) return

    const apiKeyItem = localStorage.getItem(localStorageKey)
    if (apiKeyItem) {
      try {
        const apiKeyObject: {
          apiKey?: string
          keys?: {
            products?: string
            exportCertificates?: string
            exporters?: string
            transferRequests?: string
            agency?: string
            registration?: string
            registrationAll?: string
            partyApiKey?: string
          }
          expires: string
        } = JSON.parse(apiKeyItem)

        if (apiKeyObject && apiKeyObject.expires) {
          const expireTime = new Date(apiKeyObject.expires).getTime()
          const now = new Date().getTime()

          // Check if key expires within 8 hours
          if (expireTime < now + EightHours) {
            localStorage.removeItem(localStorageKey)
            updateNewSearchApiKeyResult()
            return
          }

          setApiKey(
            apiKeyObject.apiKey || JSON.stringify(apiKeyObject.keys || {}),
          )
          scheduleRefresh(apiKeyObject.expires)
        } else {
          localStorage.removeItem(localStorageKey)
          updateNewSearchApiKeyResult()
        }
      } catch {
        localStorage.removeItem(localStorageKey)
        updateNewSearchApiKeyResult()
      }
    } else {
      updateNewSearchApiKeyResult()
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (newSearchApiKeyResult?.error) {
      console.error(
        `retrieving typesense key for ${localStorageKey} failed:`,
        newSearchApiKeyResult?.error,
      )
      setIsRefreshing(false)
      return
    }

    if (newSearchApiKeyResult && newSearchApiKeyResult.data) {
      const multiCollectionData =
        newSearchApiKeyResult.data?.newMultiCollectionApiKeysV2
      const singleKeyData =
        newSearchApiKeyResult.data?.newSearchApiKey ||
        newSearchApiKeyResult.data?.newPartyApiKey

      if (multiCollectionData) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            keys: multiCollectionData,
            expires: multiCollectionData.expires,
          }),
        )
        setApiKey(JSON.stringify(multiCollectionData))
        scheduleRefresh(multiCollectionData.expires)
      } else if (singleKeyData) {
        localStorage.setItem(
          localStorageKey,
          JSON.stringify({
            apiKey: singleKeyData.apiKey,
            expires: singleKeyData.expires,
          }),
        )
        setApiKey(singleKeyData.apiKey)
        scheduleRefresh(singleKeyData.expires)
      }
      setIsRefreshing(false)
    }
  }, [newSearchApiKeyResult.fetching])

  return { apiKey, refreshKeys, isRefreshing }
}

// 401 Error Handler for Typesense
const createTypesenseAdapterWithErrorHandling = (
  config: any,
  onUnauthorized: () => void,
) => {
  const adapter = new TypesenseInstantSearchAdapter(config)

  // Intercept search client to handle 401 errors
  const originalSearch = adapter.searchClient.search
  adapter.searchClient.search = async (requests: any) => {
    try {
      return await originalSearch.call(adapter.searchClient, requests)
    } catch (error: any) {
      if (error?.status === 401 || error?.message?.includes("401")) {
        onUnauthorized()
        throw error
      }
      throw error
    }
  }

  return adapter
}

export function useTypesenseClient(
  props: useUiTypesenseClientProps,
): TypesenseHookReturn {
  const [adapter, setAdapter] = useState<TypesenseInstantSearchAdapter>()
  const [adapterProps, setAdapterProps] =
    useState<TypesenseInstantsearchAdapterOptions>()

  const {
    typesenseHost,
    apiKey,
    multiCollectionKeysV2,
    refreshKeys: contextRefreshKeys,
  } = useContext(TypesenseContext)

  const typesenseKey = getTypesenseKey({
    context: props.context,
    collectionId: props.collectionId,
    collectionName: props.collectionName,
    searchApiKey: apiKey,
    multiCollectionKeysV2: multiCollectionKeysV2,
    usePermissions: props.usePermission,
  })

  const refreshKeys = useCallback(() => {
    if (contextRefreshKeys) {
      contextRefreshKeys()
    } else {
      clearApiKey()
      window.location.reload()
    }
  }, [contextRefreshKeys])

  const searchParamsStr = useMemo(
    () => JSON.stringify(props.searchParams || {}),
    [props.searchParams],
  )

  useEffect(() => {
    if (!typesenseKey || !typesenseHost) {
      return
    }

    const serverConfig = {
      apiKey: typesenseKey,
      sendApiKeyAsQueryParam: false,
      nodes: [
        {
          url: `${typesenseHost}/search`,
        },
      ],
    }

    const config = {
      server: serverConfig,
      additionalSearchParameters: props.searchParams || {},
    }

    const typesenseAdapter = createTypesenseAdapterWithErrorHandling(
      config,
      contextRefreshKeys || (() => {}),
    )

    setAdapter(typesenseAdapter)
    setAdapterProps(config)
  }, [typesenseKey, typesenseHost])

  return {
    adapter,
    adapterProps: adapterProps,
    apiKey: typesenseKey || "",
  }
}
